/**
 * Die Seite, die ein Zugang sieht, solange er auf seine Freischaltung wartet.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „ich will gerne, dass sich die Auftragnehmer regisrieren, aber sie noch
 *    nichts aktiv sehen oder bedienen können. kannst du noch eine
 *    willkommens/Warteseite/gesperrtseite einbinden, die ich per schalter bei
 *    en benutzern aktivieren/deaktievieren kann."
 *
 * SIE IST EINE WILLKOMMENSSEITE UND KEINE SPERRE, und das ist eine
 * Entscheidung über den Ton. Wer hier landet, hat gerade sein Passwort gesetzt
 * und ist im Begriff, für dieses Haus zu arbeiten; „Zugriff verweigert" wäre
 * die Begrüssung eines Einbrechers. Was hier steht, ist: der Zugang steht,
 * das hier ist noch zu tun, und so geht es weiter.
 *
 * UND SIE IST NICHT LEER. Sie ist der Ort, an dem er seine Anmeldung fertig
 * einrichtet: eigenes Passwort, zweiter Faktor. Eine Wartefläche, auf der man
 * nichts tun kann, ist von einem kaputten Bildschirm nicht zu unterscheiden -
 * und die beiden Handgriffe sind genau die, die er ohnehin machen muss, bevor
 * er das erste Mal wirklich arbeitet.
 *
 * DER RIEGEL SITZT IM SERVER. Diese Seite zeigt ihn an; sie ist er nicht. Wer
 * sie mit den Werkzeugen des Browsers wegräumt, kommt an keine einzige Zahl
 * heran - `OHNE_FREISCHALTUNG_ERLAUBT` in `server.ts` lässt genau die Wege
 * durch, die hier gebraucht werden.
 */

import { useEffect, useState } from 'react';
import { api, datum } from '../api.ts';
import { Marke } from '../teile/Marke.tsx';
import { FaktorEinrichtung, MeinPasswort } from './Faktor.tsx';
import type { Anmelder } from '../api.ts';

export default function Warteseite(
  { ich, abmelden, neuLaden, zurueckZuMir }:
  { ich: Anmelder; abmelden: () => void; neuLaden: () => void;
    /* Gesetzt, wenn jemand in diesen wartenden Zugang hineinversetzt ist (.213). */
    zurueckZuMir?: () => void }
) {
  const [d, setD] = useState<any>(null);
  const [was, setWas] = useState<'nichts' | 'passwort' | 'faktor'>('nichts');

  useEffect(() => {
    let weg = false;
    api.warteseite()
      .then((x) => { if (!weg) setD(x); })
      /* EIN TEXT, DER NICHT KOMMT, HÄLT DIE SEITE NICHT AUF. Sie trägt dann
         den Satz, der immer stimmt - eine weisse Fläche wäre die schlechteste
         Antwort für jemanden, der zum ersten Mal hereinkommt. */
      .catch(() => { if (!weg) setD({}); });
    return () => { weg = true; };
  }, []);

  const titel = d?.titel || 'Willkommen';
  const absaetze = String(d?.text ?? '').split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="anmeldeseite">
      <div className="wartekarte">
        <div className="karte">
          <div className="leib">
            <div className="marke">
              <Marke gross />
              <div>
                <b>MDS FTTH</b>
                <span>Auftragsverwaltung</span>
              </div>
            </div>

            <h1 style={{ marginTop: 18 }}>{titel}</h1>
            <p className="klein leise" style={{ marginTop: -6 }}>
              Angemeldet als {ich.name}
              {d?.firma ? `, ${d.firma}` : ''}
              {/* SEIT WANN, denn „seit heute" und „seit drei Wochen" sind zwei
                  verschiedene Auskünfte - und die zweite ist der Anlass, im
                  Büro nachzufragen. */}
              {d?.seit ? `, wartet seit ${datum(d.seit)}` : ''}
            </p>

            {absaetze.length
              ? absaetze.map((a: string, i: number) => (
                  <p key={i} style={{ whiteSpace: 'pre-wrap' }}>{a}</p>
                ))
              : (
                <p>
                  Ihr Zugang ist eingerichtet und noch nicht freigeschaltet.
                  Sobald der Innendienst Sie freischaltet, stehen Ihre Aufträge
                  beim nächsten Anmelden da.
                </p>
              )}

            {!!d?.kontakt && (
              <p className="klein leise">Fragen? {d.kontakt}</p>
            )}
          </div>
        </div>

        {/* WAS ER JETZT SCHON TUN KANN. Beides ist ohnehin fällig, bevor er
            das erste Mal arbeitet - hier ist der ruhigste Moment dafür. */}
        {was === 'nichts' && (
          <div className="karte">
            <div className="leib">
              <b>Solange können Sie Ihre Anmeldung fertig einrichten</b>
              <div className="reihe" style={{ marginTop: 10 }}>
                <button className="knopf" onClick={() => setWas('passwort')}>
                  Passwort ändern
                </button>
                <button className="knopf" onClick={() => setWas('faktor')}>
                  Anmeldung in 2 Schritten
                </button>
                <span className="wachsen" />
                {zurueckZuMir && (
                  <button className="knopf haupt" onClick={zurueckZuMir}>
                    Zurück zu mir{ich.echterName ? ` (${ich.echterName})` : ''}
                  </button>
                )}
                <button className="knopf" onClick={abmelden}>Abmelden</button>
              </div>
            </div>
          </div>
        )}

        {was === 'passwort' && (
          <>
            <MeinPasswort ich={ich} />
            <div className="reihe" style={{ justifyContent: 'center' }}>
              <button className="knopf" onClick={() => setWas('nichts')}>
                Zurück
              </button>
            </div>
          </>
        )}

        {was === 'faktor' && (
          <FaktorEinrichtung fertig={() => setWas('nichts')}
                             abbrechen={() => setWas('nichts')} />
        )}

        {/* EIN WEG, NACHZUSEHEN, OB ES SCHON SO WEIT IST. Ohne ihn bleibt nur
            das Abmelden und Wiederanmelden - und genau das probiert jemand,
            der wartet, alle zehn Minuten. */}
        <div className="reihe" style={{ justifyContent: 'center' }}>
          <button className="knopf leise" onClick={neuLaden}>
            Nachsehen, ob schon freigeschaltet
          </button>
        </div>
      </div>
    </div>
  );
}
